/**
 * Publish (M2 + M2.1): Upcoming (by day on phones; week calendar or table on desktop) · History (every post, newest first,
 * paged, filterable) · Needs attention (the one right action per post). `?focus=<id>` opens a post's detail. Live via SSE.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, navigate, useLocation } from "../../app/router";
import type { PublicationFilter } from "../../api/client";
import type { Publication, PublicationsPage } from "../../api/types";
import { Icon } from "../../components/Icon";
import { Button, Chip, EmptyState, ErrorNote, PageHeader, QueryView, Segmented } from "../../components/ui";
import { useApi, useQuery } from "../../data/hooks";
import { useAccounts, usePublications, useSeriesList } from "../../data/queries";
import { dayKey } from "../../lib/format";
import { useIsDesktop } from "../../lib/media";
import { PublicationDetail } from "./PublicationDetail";
import { AttentionCard, PublicationRow } from "./PublicationRow";
import { PauseBanner } from "./PublishingPause";
import { PubTable, WeekCalendar, whenOf } from "./PubViews";
import { ALL_STATES, ATTENTION_STATES, STATE_LABEL, UPCOMING_STATES } from "./pubState";

type Tab = "upcoming" | "history" | "attention";
export const HISTORY_PAGE = 50;
const time = (p: Publication) => Date.parse(whenOf(p) ?? "") || 0;

export function PublishScreen() {
  const { search } = useLocation();
  const raw = search.get("tab");
  const tab: Tab = raw === "history" || raw === "published" ? "history" : raw === "attention" ? "attention" : "upcoming";
  const focus = search.get("focus");
  const attention = usePublications({ state: ATTENTION_STATES.join(",") });
  const nAttention = attention.data?.length ?? 0;
  const go = (t: Tab) => navigate(t === "upcoming" ? "/publish" : `/publish?tab=${t}`, { replace: true });
  return (
    <div className="stack-lg">
      <PageHeader title="Publish" subtitle="Only versions you approved go out, on each queue's schedule."
        actions={<Link to="/publish/schedule" className="btn btn-primary btn-md"><Icon name="calendar" size={20} /><span>Schedule</span></Link>} />
      <PauseBanner />
      <div className="chips" role="group" aria-label="Show">
        <Chip pressed={tab === "upcoming"} onClick={() => go("upcoming")}>Upcoming</Chip>
        <Chip pressed={tab === "history"} onClick={() => go("history")}>History</Chip>
        <Chip pressed={tab === "attention"} onClick={() => go("attention")} tone={nAttention ? "attention" : undefined} count={nAttention || undefined}>Needs attention</Chip>
      </div>
      {tab === "upcoming" && <Upcoming attention={nAttention} />}
      {tab === "history" && <History />}
      {tab === "attention" && <QueryView q={attention}>{(items) => <Attention items={items} />}</QueryView>}
      {focus && <PublicationDetail key={focus} id={focus} />}
    </div>
  );
}

function Attention({ items }: { items: Publication[] }) {
  const sorted = [...items].sort((a, b) => time(a) - time(b));
  return sorted.length ? (
    <section aria-label={`Needs attention (${sorted.length})`}>
      <ul className="cards">{sorted.map((p) => <li key={p.id}><AttentionCard p={p} /></li>)}</ul>
    </section>
  ) : <EmptyState icon="check" title="Nothing needs you">Failed, held and reconnect-needed posts show up here with the one thing to do.</EmptyState>;
}

function Upcoming({ attention }: { attention: number }) {
  const desktop = useIsDesktop();
  const [view, setView] = useState<"calendar" | "list">("calendar");
  const q = usePublications({ state: UPCOMING_STATES.join(",") });
  return (
    <>
      {attention > 0 && (
        <p className="note" role="status"><Icon name="alert" size={18} /> {attention} post{attention === 1 ? " needs" : "s need"} you. <Link to="/publish?tab=attention" className="text-link">Open</Link></p>
      )}
      {desktop && <Segmented label="View" hideLabel value={view} onChange={setView} options={[{ value: "calendar", label: "Week" }, { value: "list", label: "List" }]} />}
      <QueryView q={q}>{(items) => {
        const upcoming = [...items].sort((a, b) => (a.scheduled_at ? time(a) : 0) - (b.scheduled_at ? time(b) : 0));
        if (!upcoming.length) {
          return (
            <EmptyState icon="calendar" title="Nothing scheduled" action={<Link to="/publish/schedule" className="btn btn-secondary btn-md"><span>Schedule approved videos</span></Link>}>
              Pick a series and the approved videos go into the next free slots.
            </EmptyState>
          );
        }
        if (desktop) return view === "calendar" ? <WeekCalendar items={upcoming} /> : <PubTable items={upcoming} label="Upcoming posts" initial={{ key: "when", dir: "asc" }} />;
        return <ByDay items={upcoming} />;
      }}</QueryView>
    </>
  );
}

function ByDay({ items }: { items: Publication[] }) {
  const groups = new Map<string, Publication[]>();
  for (const p of items) {
    const k = p.scheduled_at ? dayKey(p.scheduled_at) : "As soon as possible";
    groups.set(k, [...(groups.get(k) ?? []), p]);
  }
  return (
    <div className="stack" aria-label="Upcoming">
      {[...groups].map(([day, ps]) => (
        <section key={day} aria-label={day}>
          <h2 className="day-head">{day}</h2>
          <ul className="cards">{ps.map((p) => <li key={p.id}><PublicationRow p={p} /></li>)}</ul>
        </section>
      ))}
    </div>
  );
}

// ───────── History: every post, newest first, keyset-paged ─────────

type Filters = { states: string[]; series: string; account: string; from: string; to: string; q: string };
const EMPTY: Filters = { states: [], series: "", account: "", from: "", to: "", q: "" };
const dayStartIso = (d: string) => (d ? new Date(`${d}T00:00`).toISOString() : undefined);
const dayEndIso = (d: string) => { if (!d) return undefined; const x = new Date(`${d}T00:00`); x.setDate(x.getDate() + 1); return x.toISOString(); };

export function toFilter(f: Filters): PublicationFilter {
  return {
    state: f.states.length ? f.states.join(",") : undefined, series_id: f.series || undefined, account_id: f.account || undefined,
    from: dayStartIso(f.from), to: dayEndIso(f.to), q: f.q.trim() || undefined,
  };
}

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function History() {
  const api = useApi();
  const desktop = useIsDesktop();
  const series = useSeriesList().data ?? [];
  const accounts = useAccounts().data ?? [];
  const { search } = useLocation();
  // ?state=a,b (e.g. from a Home tile) pre-sets the state filter.
  const [f, setF] = useState<Filters>(() => ({ ...EMPTY, account: search.get("account") ?? "", states: (search.get("state") ?? "").split(",").filter((x) => (ALL_STATES as readonly string[]).includes(x)) }));
  const q = useDebounced(f.q);
  const filter = useMemo(() => toFilter({ ...f, q }), [f, q]);
  const key = `publications/history/${JSON.stringify(filter)}`;
  // The first page is a cached query (SSE keeps it fresh); "Load more" pages are appended locally until the filters change.
  const first = useQuery(key, () => api.pagePublications({ ...filter, order: "desc", limit: HISTORY_PAGE }), { staleMs: 15_000 });
  const [more, setMore] = useState<{ key: string; items: Publication[]; cursor: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const extra = more?.key === key ? more : null;
  const cursor = extra ? extra.cursor : first.data?.next_cursor ?? null;
  const items = useMemo(() => {
    const seen = new Set<string>();
    return [...(first.data?.publications ?? []), ...(extra?.items ?? [])].filter((p) => (seen.has(p.id) ? false : (seen.add(p.id), true)));
  }, [first.data, extra]);
  const loadMore = async () => {
    if (!cursor) return;
    setLoading(true);
    setMoreError(null);
    try {
      const page: PublicationsPage = await api.pagePublications({ ...filter, order: "desc", limit: HISTORY_PAGE, cursor });
      setMore({ key, items: [...(extra?.items ?? []), ...page.publications], cursor: page.next_cursor });
    } catch (e) {
      setMoreError(e);
    } finally {
      setLoading(false);
    }
  };
  const set = (patch: Partial<Filters>) => setF((x) => ({ ...x, ...patch }));
  const filtered = JSON.stringify(f) !== JSON.stringify(EMPTY);
  return (
    <section className="stack" aria-label="History">
      <form className="history-filters" role="search" aria-label="Filter posts" onSubmit={(e) => e.preventDefault()}>
        <label className="history-search">
          <span className="sr-only">Search titles</span>
          <input type="search" value={f.q} onChange={(e) => set({ q: e.target.value })} placeholder="Search titles, series, channel" />
        </label>
        <label><span className="field-label">Series</span>
          <select value={f.series} onChange={(e) => set({ series: e.target.value })}>
            <option value="">All series</option>
            {series.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
        </label>
        <label><span className="field-label">Channel</span>
          <select value={f.account} onChange={(e) => set({ account: e.target.value })}>
            <option value="">All channels</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        <label><span className="field-label">From</span><input type="date" value={f.from} onChange={(e) => set({ from: e.target.value })} /></label>
        <label><span className="field-label">To</span><input type="date" value={f.to} onChange={(e) => set({ to: e.target.value })} /></label>
      </form>
      <div className="chips scroll-x" role="group" aria-label="State">
        {ALL_STATES.map((s) => (
          <Chip key={s} pressed={f.states.includes(s)} onClick={() => set({ states: f.states.includes(s) ? f.states.filter((x) => x !== s) : [...f.states, s] })}>{STATE_LABEL[s]}</Chip>
        ))}
        {filtered && <button type="button" className="text-link" onClick={() => setF(EMPTY)}>Clear filters</button>}
      </div>
      <QueryView q={first}>{() => items.length ? (
        <>
          {desktop ? <PubTable items={items} label="Post history" /> : (
            <ul className="cards" aria-label="Post history">{items.map((p) => <li key={p.id}><PublicationRow p={p} showDate /></li>)}</ul>
          )}
          <p className="muted small" aria-live="polite">Showing {items.length} post{items.length === 1 ? "" : "s"}{cursor ? "" : " — that's everything"}{filtered ? " matching the filters" : ""}.</p>
          {moreError ? <ErrorNote error={moreError} onRetry={() => void loadMore()} /> : null}
          {cursor && <Button variant="secondary" block busy={loading} onClick={() => void loadMore()}>Load more</Button>}
        </>
      ) : (
        <EmptyState icon="publish" title={filtered ? "No posts match" : "No posts yet"}>{filtered ? "Try fewer filters." : "Every post you schedule shows here — planned, live, failed or cancelled — with its full history."}</EmptyState>
      )}</QueryView>
    </section>
  );
}
